import React, { useState, useEffect, useContext } from 'react'
import { Form, Row, Col, Button, Spinner } from 'react-bootstrap'
import constants from '../../../config/constants'
import { toPersonName, isValidPersonName } from '../../../utility/stringUtils'
import PhoneInput, { isPossiblePhoneNumber } from 'react-phone-number-input'
import 'react-phone-number-input/style.css'
import { Formik } from 'formik'
import * as yup from 'yup'
import { Recipient } from '../../../models/Recipient'
import { MigrantContext } from '../../../contexts/migrantContext'
import { Relationship, Countries } from '../../../config/enums'
import DefaultAddressFields from './addressFields/default'
import SingaporeAddressFields from './addressFields/singapore'
import PhilippinesAddressFields from './addressFields/philippines'

interface RecipientFormProps {
  recipient?: Recipient
  isSelf: boolean
  onSubmit: (recipient: Recipient) => void
  onCancel?: () => void
  submitButtonText: string
  cancelButtonText?: string
  isProcessing: boolean
}

const msg = constants.inlineError
const schema = yup.object({
  firstName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'First name'))
    .test('ValidPersonName', msg.auth.invalidFirstName, value => {
      return isValidPersonName(value)
    }),
  lastName: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Last name'))
    .test('ValidPersonName', msg.auth.invalidLastName, value => {
      return isValidPersonName(value)
    }),
  phonenumber: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Phonenumber'))
    .test('ValidPhonenumber', msg.auth.invalidPhonenumber, value => {
      return isPossiblePhoneNumber(value)
    }),
  email: yup.string().email(msg.auth.invalidEmail),
  // email: yup
  //   .string()
  //   .email(msg.auth.invalidEmail)
  //   .required(msg.fieldRequired.replace('%s', 'Email')),
  relationship: yup
    .string()
    .required(msg.fieldRequired.replace('%s', 'Relationship')),
  address1: yup.string().required(msg.fieldRequired.replace('%s', 'Address 1')),
  address2: yup.string().required(msg.fieldRequired.replace('%s', 'Address 2')),
  city: yup.string().required(msg.fieldRequired.replace('%s', 'City')),
  region: yup.string().required(msg.fieldRequired.replace('%s', 'Region')),
  country: yup.string().required(msg.fieldRequired.replace('%s', 'Country')),
  zipCode: yup.string().required(msg.fieldRequired.replace('%s', 'Postal code'))
})

const RecipientForm: React.FC<RecipientFormProps> = ({
  recipient,
  isSelf,
  onSubmit,
  onCancel,
  submitButtonText,
  cancelButtonText,
  isProcessing
}) => {
  const profile = useContext(MigrantContext)?.profile
  const country = useContext(MigrantContext)?.shopCountry?.name
  const [isValidNumber, setIsValidNumber] = useState(false)

  const defaultValue = country === Countries.singapore ? 'N/A' : ''
  const initialValues = {
    firstName: recipient?.firstName ?? (isSelf ? profile?.firstName ?? '' : ''),
    lastName: recipient?.lastName ?? (isSelf ? profile?.lastName ?? '' : ''),
    phonenumber:
      recipient?.phone ?? (isSelf ? profile?.phonenumber ?? '+65' : '+65'),
    email: recipient?.email ?? (isSelf ? profile?.email ?? '' : ''),
    relationship: recipient?.relationship ?? (isSelf ? Relationship.self : ''),
    address1:
      recipient?.address.address1 ??
      (isSelf ? profile?.address?.address1 ?? '' : ''),
    address2:
      recipient?.address.address2 ??
      (isSelf ? profile?.address?.address2 ?? defaultValue : defaultValue),
    city:
      recipient?.address.city ??
      (isSelf ? profile?.address?.city ?? defaultValue : defaultValue),
    region:
      recipient?.address.region ??
      (isSelf ? profile?.address?.region ?? defaultValue : defaultValue),
    country: country ?? '',
    zipCode:
      recipient?.address.zipCode ??
      (isSelf ? profile?.address?.zipCode ?? '' : '')
  }

  useEffect(() => {
    if (recipient) {
      setIsValidNumber(isPossiblePhoneNumber(recipient.phone))
    }
  }, [recipient])

  return (
    <Formik
      validationSchema={schema}
      onSubmit={values => {
        onSubmit({
          id: recipient?.id ?? '',
          firstName: values.firstName,
          lastName: values.lastName,
          phone: values.phonenumber,
          email: values.email,
          relationship: values.relationship,
          address: {
            address1: values.address1,
            address2: values.address2,
            city: values.city,
            region: values.region,
            country: values.country,
            zipCode: values.zipCode
          }
        })
      }}
      initialValues={initialValues}
      enableReinitialize={true}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        touched,
        setFieldValue,
        setFieldTouched
      }) => (
        <Form noValidate onSubmit={handleSubmit} data-testid="recipientForm">
          <div
            data-testid="form-recipientSection"
            className={isSelf ? 'd-none' : ''}
          >
            <Row>
              <Col>
                <h5 className="subSectionTitle">Recipient details</h5>
              </Col>
            </Row>
            <Form.Group>
              <Form.Label
                className="mochi-recipient-label"
                htmlFor="recipient-first-name"
              >
                First Name
              </Form.Label>
              <Form.Control
                id="recipient-first-name"
                type="text"
                name="firstName"
                placeholder="Enter first name"
                value={values.firstName}
                onBlur={handleBlur}
                isInvalid={touched.firstName && !!errors.firstName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const fName = toPersonName(e.target.value)
                  setFieldValue('firstName', fName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.firstName}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <Form.Label
                className="mochi-recipient-label"
                htmlFor="recipient-last-name"
              >
                Last Name
              </Form.Label>
              <Form.Control
                id="recipient-last-name"
                type="text"
                name="lastName"
                placeholder="Enter last name"
                value={values.lastName}
                onBlur={handleBlur}
                isInvalid={touched.lastName && !!errors.lastName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const lName = toPersonName(e.target.value)
                  setFieldValue('lastName', lName)
                }}
              />
              <Form.Control.Feedback type="invalid">
                {errors.lastName}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <Form.Label htmlFor="recipient-relationship">
                Relationship
              </Form.Label>
              <Form.Control
                id="recipient-relationship"
                type="text"
                name="relationship"
                placeholder="e.g. cousin"
                value={values.relationship}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.relationship && !!errors.relationship}
              />
              <Form.Control.Feedback type="invalid">
                {errors.relationship}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <Form.Label
                className="mochi-recipient-label"
                htmlFor="recipient-email"
              >
                Email
              </Form.Label>
              <Form.Control
                id="recipient-email"
                type="email"
                name="email"
                placeholder="Enter email"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                isInvalid={touched.email && !!errors.email}
              />
              <Form.Control.Feedback type="invalid">
                {errors.email}
              </Form.Control.Feedback>
            </Form.Group>
            <Form.Group>
              <Form.Label
                className="mochi-recipient-label"
                htmlFor="recipient-phonenumber"
              >
                Phone number
              </Form.Label>
              <PhoneInput
                id="recipient-phonenumber"
                international
                defaultCountry="SG"
                value={values.phonenumber}
                className={
                  'form-control' +
                  (touched.phonenumber ? ' touched' : '') +
                  (isValidNumber ? '' : ' invalid')
                }
                onChange={number => {
                  setFieldValue('phonenumber', number)
                  setFieldTouched('phonenumber')
                  setIsValidNumber(isPossiblePhoneNumber(number))
                }}
              />
              {touched.phonenumber && !isValidNumber && (
                <div className="invalid-feedback" style={{ display: 'block' }}>
                  {msg.auth.invalidPhonenumber}
                </div>
              )}
            </Form.Group>
          </div>

          <div data-testid="form-addressSection">
            <Row>
              <Col>
                <h5 className="subSectionTitle">Shipping details</h5>
              </Col>
            </Row>

            {(() => {
              if (country === Countries.singapore) {
                return (
                  <SingaporeAddressFields
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                  />
                )
              } else if (country === Countries.philippines) {
                return (
                  <PhilippinesAddressFields
                    values={values}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                  />
                )
              } else {
                return (
                  <DefaultAddressFields
                    values={values}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    touched={touched}
                  />
                )
              }
            })()}
          </div>

          <div className="text-center mt-4">
            {cancelButtonText ? (
              <>
                <Button
                  variant="light"
                  size="sm"
                  className="mr-3"
                  data-testid="cancelButton"
                  onClick={onCancel}
                  disabled={isProcessing}
                >
                  {cancelButtonText}
                </Button>
                <Button
                  size="sm"
                  type="submit"
                  data-testid="nextButton"
                  disabled={isProcessing}
                >
                  {isProcessing ? (
                    <>
                      <span className="mr-2">
                        <Spinner animation="border" size="sm" />
                      </span>
                      Loading...
                    </>
                  ) : (
                    <>{submitButtonText}</>
                  )}
                </Button>
              </>
            ) : (
              <Button
                variant="secondary"
                type="submit"
                data-testid="createRecipientButton"
                disabled={isProcessing}
              >
                {isProcessing ? (
                  <>
                    <span className="mr-2">
                      <Spinner animation="border" size="sm" />
                    </span>
                    Saving...
                  </>
                ) : (
                  <>{submitButtonText}</>
                )}
              </Button>
            )}
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default RecipientForm
